import { useEffect, useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import WCButton from "@/components/WCButton";

// Cinematic hero: slow Ken-Burns still zoom + staggered word drop + grain overlay.
// The nav (from parent layout) stays — but the content fades in word-by-word so the
// first thing visitors see is pure imagery, then language arrives.
export default function GuatemalaHero({ image, words, subtext, ctaHref }) {
  const [phase, setPhase] = useState(0); // 0 = black, 1 = image, 2 = words, 3 = chrome
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 150);
    const t2 = setTimeout(() => setPhase(2), 900);
    const t3 = setTimeout(() => setPhase(3), 900 + words.length * 220 + 400);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, [words.length]);

  return (
    <section
      className="relative w-full h-screen overflow-hidden bg-black"
      data-testid="guatemala-hero"
    >
      {/* Image — Ken-Burns slow zoom-in, fades in after the initial black frame */}
      <div
        className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
          phase >= 1 ? "opacity-100" : "opacity-0"
        }`}
      >
        <img
          src={image}
          alt="Guatemala"
          className="wc-image absolute inset-0 w-full h-full object-cover gt-kenburns"
        />
        {/* Warm tint toward volcanic reds, subtle */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/45 to-black/80" />
        {/* Film grain */}
        <div className="absolute inset-0 gt-grain pointer-events-none" />
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_200px_80px_rgba(0,0,0,0.9)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <div className="max-w-6xl">
          <h1
            className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-widest leading-[1.05]"
            data-testid="guatemala-hero-heading"
          >
            {words.map((w, i) => (
              <span
                key={i}
                className={`inline-block mx-2 md:mx-3 transition-all duration-[700ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
                  phase >= 2
                    ? "opacity-100 translate-y-0 blur-0"
                    : "opacity-0 translate-y-6 blur-[8px]"
                }`}
                style={{ transitionDelay: `${i * 220}ms` }}
              >
                {w}
              </span>
            ))}
          </h1>
        </div>

        <div
          className={`transition-opacity duration-700 ${
            phase >= 3 ? "opacity-100" : "opacity-0"
          }`}
        >
          <p
            className="wc-label text-white/70 text-xs md:text-sm mt-10 tracking-[0.35em]"
            data-testid="guatemala-hero-coords"
          >
            LAT 14° 37′ N &nbsp;/&nbsp; LONG 90° 31′ W &nbsp;—&nbsp;{" "}
            <span className="gt-red">VOL. 1</span> &nbsp;—&nbsp; DROPS JULY 2026
          </p>
          <div className="mt-8">
            <StatusBadge status="coming_soon" />
          </div>
          <div className="mt-8">
            <WCButton as="a" href={ctaHref} data-testid="guatemala-hero-cta">
              Join The Waitlist
            </WCButton>
          </div>
          {subtext && (
            <p className="wc-label text-[10px] text-white/40 mt-10 tracking-[0.4em]">
              {subtext}
            </p>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-10 left-1/2 -translate-x-1/2 wc-label text-[10px] text-white/50 tracking-[0.4em] transition-opacity duration-700 ${
          phase >= 3 ? "opacity-100" : "opacity-0"
        }`}
      >
        SCROLL
        <div className="w-px h-10 mx-auto mt-3 bg-white/50 gt-scroll-line" />
      </div>
    </section>
  );
}
